<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Navigation/NavigationSidebar.vue
import type { Schemas } from '@shopware/api-client/api-types';
import { StaticNavigation } from '~/utils/staticNavigation';
import type { StaticNavigationItem } from '../../types/staticNavigation';

const { languageIdChain } = useSessionContext();
const navigationStore = useNavigationStore();
const { mainNavigationElements } = storeToRefs(navigationStore);

const sideMenuController = useModal();

// shownNavigationItems stores the nav items that should be shown at the moment, navigationElements initially
// will be updated with the child categories when a category with children is selected
const shownNavigationItems = ref(mainNavigationElements.value);

// previousNavigationItems stores the nav items that got selected and whoes children get displayed
// used to navigate back and display the link above the children
const previousNavigationItems = ref<Schemas['Category'][]>([]);

watch(
    languageIdChain,
    async () => {
        await navigationStore.loadMainNavigation(2);
        shownNavigationItems.value = mainNavigationElements.value;
        previousNavigationItems.value = [];
    },
    { immediate: true },
);

const handleClick = async (navigationElement, categoryLink) => {
    if (navigationElement.childCount > 0) {
        previousNavigationItems.value.push(navigationElement);
        shownNavigationItems.value = navigationElement.children;
        return;
    }

    sideMenuController.close();
    await navigateTo(categoryLink);
};

const handleBack = () => {
    // IDS-188: if static sub category is shown, revert this
    if (subCategoryIsShown.value) {
        subCategoryIsShown.value = false;
        shownStaticNavigation.value = StaticNavigation;
        return;
    }

    // IDS-188: if we have no previous item -> we assume that we go back to the static stuff
    if (previousNavigationItems.value.length === 0) {
        showShopCategories.value = false;
        shownNavigationItems.value = mainNavigationElements.value;
        return;
    }

    if (previousNavigationItems.value.length <= 1) {
        shownNavigationItems.value = mainNavigationElements.value;
        previousNavigationItems.value.pop();
        return;
    }

    previousNavigationItems.value.pop();
    shownNavigationItems.value = previousNavigationItems.value.at(-1)?.children;
};

const lastPreviousItem = computed(() =>
    previousNavigationItems.value.length > 0 && previousNavigationItems.value.at(-1)
        ? previousNavigationItems.value.at(-1)
        : null,
);

// IDS-188: static navigation structure
const showShopCategories = ref(false);
const shownStaticNavigation = ref(StaticNavigation);
const subCategoryIsShown = ref(false);

const changeStaticNavigation = (snippet: string) => {
    // if the user clicks on "shop", we want to show the default shopware navigation
    if (snippet === 'shop') {
        shownStaticNavigation.value = StaticNavigation;
        showShopCategories.value = true;
        subCategoryIsShown.value = false;
        return;
    }

    // we want to show the subcategory and also all children of this one
    const newStaticNavigation = StaticNavigation.find(
        (item: StaticNavigationItem) => item.snippet === snippet,
    )?.children?.at(0);
    if (!newStaticNavigation) return;

    shownStaticNavigation.value = [newStaticNavigation, ...newStaticNavigation.children];
    subCategoryIsShown.value = true;
};
</script>

<template>
    <!-- IDS-124: change size of the bar icon-->
    
<FormKitLazyProvider config-file="true">
<FormKitIcon
        icon="bars"
        class="h-6 w-6 cursor-pointer md:hidden"
        @click="sideMenuController.open()"
    />
    <LazyLayoutSidebar
        v-if="sideMenuController.isOpen"
        :controller="sideMenuController"
    >
        <template #header>
            <!-- IDS-188: move the if condition to a template -> we need an empty div at least for styling-->
            <div
                class="flex cursor-pointer items-center gap-2"
                @click="handleBack()"
            >
                <template v-if="previousNavigationItems.length > 0 || showShopCategories || subCategoryIsShown">
                    <!-- IDS-188: change icon, change style -->
                    <FormKitIcon
                        icon="ids_arrow-left"
                        class="block size-6"
                    />
                    <span>{{ $t('navigation.sidebar.backLinkLabel') }}</span>
                </template>
            </div>
        </template>

        <template #content>
            <!-- IDS-188: change style -->
            <div
                v-if="!showShopCategories"
                class="grid flex-grow gap-2 px-6"
            >
                <!-- IDS-188: add static navigation -->
                <NavigationMobileStaticItem
                    v-for="item in shownStaticNavigation"
                    :key="item.snippet"
                    :static-navigation-item="item"
                    :sub-category-is-shown="subCategoryIsShown"
                    class="flex cursor-pointer items-center justify-between border-b border-neutral-400 py-6 text-lg font-extrabold last:border-b-0"
                    @change="changeStaticNavigation"
                />
            </div>

            <!-- display the current category -->
            <!-- IDS-188: remove display of current category name -->
            <div
                v-else
                class="grid flex-grow gap-2 px-6"
            >
                <!-- display a link to the current category with all items snippet -->
                <NavigationLink
                    v-if="lastPreviousItem"
                    :navigation-element="lastPreviousItem"
                    classes="border-b border-neutral-400 py-6 font-extrabold text-lg cursor-pointer"
                    :as-all-items-link="true"
                    @click="sideMenuController.close()"
                />

                <!-- display the child categories -->
                <!-- IDS-188: we need to move the div outside the component -> otherwise its sorted false -->
                <div
                    v-for="navigationElement in shownNavigationItems"
                    :key="navigationElement.id"
                    class="cursor-pointer border-b border-neutral-400 py-6 text-lg font-extrabold last:border-b-0"
                >
                    <NavigationLink
                        :navigation-element="navigationElement"
                        active-classes="font-bold md:border-b-2 md:border-brand-primary"
                        :as-link="navigationElement.childCount === 0"
                        :display-icon="navigationElement.childCount > 0"
                        @click="
                            (navigationElement: Schemas['Category'], categoryLink: string | null) =>
                                handleClick(navigationElement, categoryLink)
                        "
                    />
                </div>
            </div>
        </template>
    </LazyLayoutSidebar>
</FormKitLazyProvider>

</template>
